
import React, { useState } from 'react';
import { complianceService } from '../../services/api';

// PapaParse from CDN to avoid build issues
declare const Papa: any;

interface BulkCertImportModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

const normalise = (s: string) => (s || '').toLowerCase().replace(/[,]/g, '').replace(/\s+/g, ' ').trim();

const sha256 = async (buf: ArrayBuffer): Promise<string> => {
    const h = await crypto.subtle.digest('SHA-256', buf);
    return Array.from(new Uint8Array(h)).map((b) => b.toString(16).padStart(2, '0')).join('');
};

export default function BulkCertImportModal({ isOpen, onClose, onSuccess }: BulkCertImportModalProps) {
    const [file, setFile] = useState<File | null>(null);
    const [zipFile, setZipFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<string | null>(null);
    const [report, setReport] = useState<{ total: number; added: number; failed: number; matched: number; errors: string[] } | null>(null);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setFile(e.target.files[0]);
            setReport(null);
        }
    };

    const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setZipFile(e.target.files[0]);
        }
    };

    const handleUpload = () => {
        if (!file) return;

        // Guard: PapaParse must be loaded from CDN
        if (typeof Papa === 'undefined') {
            alert('CSV parser not loaded. Please refresh the page and try again.');
            return;
        }

        setUploading(true);
        setUploadProgress(null);

        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: async (results: any) => {
                const rows = results.data;
                try {
                    // Normalize headers and match fields
                    let certificates = rows.map((row: any) => ({
                        address: row.address || row.propertyAddress || row.Address || '',
                        postcode: row.postcode || row.Postcode || '',
                        type: row.type || row.certificateType || row.Type || 'Miscellaneous',
                        expiryDate: row.expiryDate || row.expires || row.Expiry || row.Expires || '',
                        issuedDate: row.issuedDate || row.issued || row.Issued || '',
                        notes: row.notes || row.Notes || 'Bulk metadata import',
                        pdf_filename: row.pdf_filename || row.pdfFilename || '',
                    }));

                    if (certificates.length === 0) {
                        setReport({ total: 0, added: 0, failed: 0, matched: 0, errors: ['No valid rows found in CSV. Check your column headers.'] });
                        setUploading(false);
                        return;
                    }

                    // If a companion zip was provided, upload each matched document
                    // to S3 via a presigned URL and attach the real file reference
                    // to its row before sending to the backend. Matches the same
                    // pdf_filename -> zip-entry pattern used for per-property
                    // certificate import (CertificateList.tsx).
                    if (zipFile) {
                        setUploadProgress('Reading zip file…');
                        const JSZip = (await import('jszip')).default;
                        const zip = await JSZip.loadAsync(zipFile);
                        const index: Record<string, any> = {};
                        zip.forEach((path: string, entry: any) => {
                            if (!entry.dir) {
                                const base = path.split('/').pop() || path;
                                index[base.toLowerCase()] = entry;
                            }
                        });

                        // Resolve each row's property client-side (same address+
                        // postcode matching the backend does) so we know which
                        // property to scope the presigned upload to.
                        const propsRes = await complianceService.getProperties();
                        const properties = propsRes.data?.data || propsRes.data || [];
                        const addressMap: Record<string, string> = {};
                        properties.forEach((p: any) => {
                            const addr = typeof p.address === 'object' ? p.address?.line1 : p.address;
                            if (addr) addressMap[normalise(addr)] = p.id;
                            if (addr && p.postcode) addressMap[normalise(`${addr} ${p.postcode}`)] = p.id;
                        });

                        const enriched = [];
                        let uploadedCount = 0;
                        for (const row of certificates) {
                            const wanted = String(row.pdf_filename || '').trim();
                            if (!wanted) { enriched.push(row); continue; }

                            const entry = index[wanted.toLowerCase()];
                            if (!entry) { enriched.push({ ...row, _attachStatus: 'no-pdf-in-zip' }); continue; }

                            const normFull = normalise(`${row.address} ${row.postcode}`);
                            const normAddr = normalise(row.address);
                            const propertyId = addressMap[normFull] || addressMap[normAddr];
                            if (!propertyId) { enriched.push({ ...row, _attachStatus: 'no-matching-property' }); continue; }

                            try {
                                setUploadProgress(`Uploading ${wanted} (${uploadedCount + 1})…`);
                                const blob = await entry.async('blob');
                                const inferType = wanted.toLowerCase().endsWith('.pdf') ? 'application/pdf'
                                    : wanted.toLowerCase().match(/\.(jpe?g)$/) ? 'image/jpeg'
                                    : wanted.toLowerCase().endsWith('.png') ? 'image/png'
                                    : blob.type || 'application/octet-stream';
                                const uploadFile = new File([blob], wanted, { type: inferType });

                                const pres = await complianceService.getPresignedUploadUrl({
                                    fileName: uploadFile.name,
                                    fileType: uploadFile.type,
                                    propertyId,
                                });
                                const { uploadUrl, s3Key } = pres.data?.data || {};
                                if (!uploadUrl || !s3Key) throw new Error('no presigned URL');

                                const ab = await uploadFile.arrayBuffer();
                                const putRes = await fetch(uploadUrl, { method: 'PUT', body: ab, headers: { 'Content-Type': uploadFile.type } });
                                if (!putRes.ok) throw new Error('S3 PUT ' + putRes.status);

                                const hash = await sha256(ab);
                                enriched.push({ ...row, s3Key, fileName: uploadFile.name, contentHash: hash, _attachStatus: 'attached' });
                                uploadedCount++;
                            } catch (uploadErr: any) {
                                console.warn('[CertBulk] PDF upload failed for', wanted, uploadErr?.message);
                                enriched.push({ ...row, _attachStatus: 'upload-failed' });
                            }
                        }
                        certificates = enriched;
                        setUploadProgress(null);
                    }

                    const res = await complianceService.bulkImportCertificatesGlobal(certificates);
                    setReport(res.data.data);
                    if (onSuccess) onSuccess();
                } catch (err: any) {
                    setReport({
                        total: rows.length,
                        added: 0,
                        failed: rows.length,
                        matched: 0,
                        errors: [err.response?.data?.message || err.message]
                    });
                } finally {
                    setUploading(false);
                    setUploadProgress(null);
                }
            },
            error: (err: any) => {
                setReport({ total: 0, added: 0, failed: 0, matched: 0, errors: [`CSV parse error: ${err.message}`] });
                setUploading(false);
            }
        });
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden border border-gray-200">
                <div className="bg-gray-50 px-6 py-4 border-b flex justify-between items-center">
                    <h3 className="text-xl font-bold text-gray-900">Bulk Certificate Import</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition">
                        <i className="fas fa-times text-lg"></i>
                    </button>
                </div>

                <div className="p-8">
                    {!report ? (
                        <>
                            <div className="bg-emerald-50 border border-emerald-100 p-5 rounded-xl mb-6">
                                <div className="flex">
                                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center mr-4 text-emerald-600">
                                        <i className="fas fa-file-csv"></i>
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-black text-emerald-900 uppercase">Migration Mode</h4>
                                        <p className="text-sm text-emerald-800 mt-1">
                                            Import certificates from your master list. Properties will be matched by address automatically.
                                            Add a <b>pdf_filename</b> column and a companion .zip of the actual documents to attach the
                                            real certificate files, not just their dates.
                                        </p>
                                    </div>
                                </div>
                                <div className="mt-4 grid grid-cols-2 gap-4 text-xs font-medium text-emerald-700">
                                    <div className="flex items-center"><i className="fas fa-check-circle mr-2"></i> address</div>
                                    <div className="flex items-center"><i className="fas fa-check-circle mr-2"></i> postcode</div>
                                    <div className="flex items-center"><i className="fas fa-check-circle mr-2"></i> type (Gas, EPC, EICR)</div>
                                    <div className="flex items-center"><i className="fas fa-check-circle mr-2"></i> expiryDate</div>
                                    <div className="flex items-center"><i className="fas fa-check-circle mr-2"></i> pdf_filename (optional)</div>
                                </div>
                            </div>

                            <div className="border-2 border-dashed border-gray-200 rounded-2xl p-10 text-center hover:border-emerald-500 hover:bg-emerald-50/30 transition-all cursor-pointer relative group">
                                <input
                                    type="file"
                                    accept=".csv"
                                    onChange={handleFileChange}
                                    className="absolute inset-0 opacity-0 cursor-pointer"
                                />
                                <i className="fas fa-cloud-upload-alt text-4xl text-gray-300 group-hover:text-emerald-500 mb-4 transition-colors"></i>
                                <h4 className="text-base font-bold text-gray-900">
                                    {file ? file.name : 'Select CSV Master List'}
                                </h4>
                                <p className="text-xs text-gray-500 mt-1">Drag and drop your spreadsheet here</p>
                            </div>

                            <div className="border-2 border-dashed border-gray-200 rounded-2xl p-6 text-center hover:border-blue-500 hover:bg-blue-50/30 transition-all cursor-pointer relative group mt-4">
                                <input
                                    type="file"
                                    accept=".zip"
                                    onChange={handleZipChange}
                                    className="absolute inset-0 opacity-0 cursor-pointer"
                                />
                                <i className="fas fa-file-zipper text-2xl text-gray-300 group-hover:text-blue-500 mb-2 transition-colors"></i>
                                <h4 className="text-sm font-bold text-gray-900">
                                    {zipFile ? zipFile.name : 'Optional: Select a .zip of the actual certificate documents'}
                                </h4>
                                <p className="text-xs text-gray-500 mt-1">Each file's name must match a pdf_filename value in your CSV</p>
                            </div>

                            {uploadProgress && (
                                <p className="text-xs text-blue-600 mt-3 text-center">{uploadProgress}</p>
                            )}

                            <div className="flex justify-between items-center mt-8 pt-6 border-t font-sans">
                                <button
                                    onClick={() => {
                                        const csv = "address,postcode,type,expiryDate,issuedDate,notes,pdf_filename\n123 Street Name,SW1A 1AA,EPC,2026-05-15,2016-05-15,Legacy Cert,epc_123_street.pdf";
                                        const blob = new Blob([csv], { type: 'text/csv' });
                                        const url = window.URL.createObjectURL(blob);
                                        const a = document.createElement('a');
                                        a.href = url;
                                        a.download = 'certificate_import_template.csv';
                                        a.click();
                                    }}
                                    className="text-xs font-black text-emerald-600 uppercase tracking-widest hover:text-emerald-700"
                                >
                                    <i className="fas fa-download mr-1"></i> Download Template
                                </button>
                                <button
                                    onClick={handleUpload}
                                    disabled={!file || uploading}
                                    className="bg-emerald-600 text-white px-8 py-3 rounded-xl font-bold hover:bg-emerald-700 disabled:opacity-50 shadow-lg shadow-emerald-200 transition-all"
                                >
                                    {uploading ? 'Processing List...' : 'Start Import'}
                                </button>
                            </div>
                        </>
                    ) : (
                        <div className="text-center">
                            <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 ${report.failed === 0 ? 'bg-green-100 text-green-600' : 'bg-amber-100 text-amber-600'}`}>
                                <i className={`fas fa-${report.failed === 0 ? 'check' : 'clipboard-list'} text-3xl`}></i>
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-2">Processing Complete</h3>

                            <div className="grid grid-cols-2 gap-4 my-8">
                                <div className="bg-gray-50 p-4 rounded-xl border">
                                    <p className="text-xs text-gray-500 uppercase font-black mb-1">Total</p>
                                    <p className="text-2xl font-bold">{report.total}</p>
                                </div>
                                <div className="bg-emerald-50 p-4 rounded-xl border border-emerald-100">
                                    <p className="text-xs text-emerald-600 uppercase font-black mb-1">Imported</p>
                                    <p className="text-2xl font-bold text-emerald-700">{report.added}</p>
                                </div>
                                <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
                                    <p className="text-xs text-blue-600 uppercase font-black mb-1">Stitched</p>
                                    <p className="text-2xl font-bold text-blue-700">{report.matched}</p>
                                </div>
                                <div className="bg-red-50 p-4 rounded-xl border border-red-100">
                                    <p className="text-xs text-red-600 uppercase font-black mb-1">Failed</p>
                                    <p className="text-2xl font-bold text-red-700">{report.failed}</p>
                                </div>
                            </div>

                            {report.errors.length > 0 && (
                                <div className="text-left bg-red-50 p-4 rounded-xl border border-red-100 max-h-40 overflow-y-auto mb-6">
                                    <p className="text-xs font-bold text-red-800 mb-2 underline uppercase">Issues to Resolve:</p>
                                    {report.errors.map((e, idx) => (
                                        <div key={idx} className="text-xs text-red-700 border-b border-red-100 py-1 last:border-0">{e}</div>
                                    ))}
                                </div>
                            )}

                            <div className="flex space-x-3 justify-center">
                                <button
                                    onClick={onClose}
                                    className="bg-gray-900 text-white px-8 py-3 rounded-xl font-bold hover:bg-gray-800 transition-all"
                                >
                                    Dismiss
                                </button>
                                {report.failed > 0 && (
                                    <button
                                        onClick={() => setReport(null)}
                                        className="bg-white text-gray-700 border border-gray-200 px-8 py-3 rounded-xl font-bold hover:bg-gray-50 transition-all"
                                    >
                                        Try Again
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
